import React, { useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Home, Users, Settings, LogOut, Menu, X, ShieldCheck, Wallet,
  History, ListChecks,
} from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useBrand, useTerms } from "../lib/BrandContext";
import { withReturnTo } from "../lib/backNavigation";
import { Button } from "./ui/button";
import InstallPrompt from "./InstallPrompt";
import VerifyEmailBanner from "./VerifyEmailBanner";

// Coach nav labels are built dynamically so they reflect the org's
// Industry Profile (Teams / Classes / Dojos / Programs / …).
const buildCoachNav = (terms) => [
  { to: "/coach/dashboard", label: "Home",              short: "Home",              icon: Home,     mobile: true },
  { to: "/coach/teams",     label: terms.group_plural,  short: terms.group_plural,  icon: Users,    mobile: true },
  { to: "/coach/payments",  label: "Payments",          short: "Payments",          icon: Wallet,   mobile: true },
  { to: "/coach/settings",  label: "Settings",          short: "Settings",          icon: Settings, mobile: true },
];

const PARENT_NAV = [
  { to: "/parent/dashboard", label: "Home", short: "Home", icon: Home, mobile: true },
  { to: "/parent/owed", label: "What I Owe", short: "Pay", icon: ListChecks, mobile: true },
  { to: "/parent/history", label: "History", short: "History", icon: History, mobile: true },
];

const ADMIN_NAV = [
  { to: "/admin/dashboard", label: "Admin", short: "Admin", icon: Home, mobile: true },
];

function DesktopNavItems({ items, onClick }) {
  return items.map(({ to, label, icon: Icon }) => (
    <NavLink
      key={to}
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${
          isActive ? "bg-brand-50 text-brand" : "text-ink-700 hover:bg-ink-100"
        }`
      }
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <Icon className="h-5 w-5" />
      {label}
    </NavLink>
  ));
}

function BottomTabBar({ items }) {
  const visible = items.filter((i) => i.mobile);
  return (
    <nav
      data-testid="mobile-bottom-tabs"
      className="fixed inset-x-0 bottom-0 z-40 grid border-t border-ink-100 bg-white/95 shadow-[0_-1px_0_rgba(15,23,42,0.04)] backdrop-blur md:hidden"
      style={{
        gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))`,
        paddingBottom: "max(env(safe-area-inset-bottom), 0px)",
      }}
    >
      {visible.map(({ to, short, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to.endsWith("/dashboard")}
          className={({ isActive }) =>
            `flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-bold uppercase tracking-wide transition-colors ${
              isActive ? "text-brand" : "text-ink-500 hover:text-ink-900"
            }`
          }
          data-testid={`tab-${short.toLowerCase()}`}
        >
          <Icon className="h-6 w-6" />
          {short}
        </NavLink>
      ))}
    </nav>
  );
}

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const { terms } = useTerms();
  const navigate = useNavigate();
  const location = useLocation();
  const [more, setMore] = useState(false);

  const baseNav = user?.role === "coach" ? buildCoachNav(terms) : user?.role === "parent" ? PARENT_NAV : ADMIN_NAV;

  // If the coach is currently inside a specific team, augment the Settings
  // nav link so it carries `?from=/coach/teams/{id}&from_label={team.name}`.
  // Settings.jsx reads that via useSmartBack() and renders "Back to <team>".
  // For any other location (bottom-nav from Dashboard, Payments, etc.) the
  // nav link stays a plain "/coach/settings" — no contextual Back is shown.
  //
  // The team name is read from the sessionStorage cache TeamDetail writes
  // on mount. If the cache is empty (fresh page load into /coach/teams/:id
  // before TeamDetail has finished loading), we still attach the `from` URL
  // and let SmartBack fall back to the default "Team" label.
  const nav = useMemo(() => {
    if (user?.role !== "coach") return baseNav;
    const teamMatch = location.pathname.match(/^\/coach\/teams\/([^/?]+)$/);
    if (!teamMatch) return baseNav;
    const teamId = teamMatch[1];
    const fromPath = `/coach/teams/${teamId}`;
    let label = terms.Team || "Team";
    try {
      const cached = JSON.parse(sessionStorage.getItem("sf_current_team") || "null");
      if (cached && cached.id === teamId && cached.name) label = cached.name;
    } catch { /* sessionStorage disabled — fall back to default label */ }
    return baseNav.map((item) =>
      item.to === "/coach/settings"
        ? { ...item, to: withReturnTo(item.to, { from: fromPath, label }) }
        : item,
    );
  }, [baseNav, location.pathname, terms.Team, user?.role]);

  const hasMore = nav.some((i) => !i.mobile);

  const homePath =
    user?.role === "coach" ? "/coach/dashboard" :
    user?.role === "parent" ? "/parent/dashboard" :
    "/admin/dashboard";

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen bg-ink-100/40">
      {/* Sidebar (desktop only) */}
      <aside className="hidden w-64 shrink-0 border-r border-ink-100 bg-white md:flex md:flex-col">
        <Link
          to={homePath}
          className="flex h-16 items-center gap-2 border-b border-ink-100 px-5 font-heading text-lg font-bold text-ink-900 transition-colors hover:bg-ink-100/60"
          data-testid="sidebar-brand-link"
          aria-label={`Go to ${brand.app_name} home`}
        >
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white shadow-sm">
            <ShieldCheck className="h-5 w-5" />
          </span>
          {brand.app_name}
        </Link>
        <nav className="flex-1 space-y-1 p-3">
          <DesktopNavItems items={nav} />
        </nav>
        <div className="border-t border-ink-100 p-3">
          <div className="mb-2 rounded-lg bg-ink-100 px-3 py-2">
            <p className="truncate text-sm font-semibold text-ink-900" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="truncate text-xs text-ink-500">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            onClick={doLogout}
            className="w-full justify-start gap-2 text-ink-700 hover:bg-ink-100"
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" /> Log out
          </Button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile topbar (compact, brand + More) */}
        <header
          className="sticky top-0 z-30 flex items-center justify-between border-b border-ink-100 bg-white px-4 md:hidden"
          style={{
            paddingTop: "max(env(safe-area-inset-top), 0px)",
            height: "calc(3.5rem + max(env(safe-area-inset-top), 0px))",
          }}
        >
          <Link to={homePath} className="flex items-center gap-2 font-heading text-base font-bold text-ink-900" data-testid="mobile-brand-link">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-brand text-white">
              <ShieldCheck className="h-4 w-4" />
            </span>
            {brand.app_name}
          </Link>
          {hasMore ? (
            <button
              type="button"
              onClick={() => setMore(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-ink-100 text-ink-700"
              data-testid="mobile-more-button"
              aria-label="More"
            >
              <Menu className="h-5 w-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={doLogout}
              className="inline-flex h-10 px-3 items-center justify-center rounded-lg border border-ink-100 text-xs font-semibold text-ink-700"
              data-testid="mobile-logout-button"
            >
              Log out
            </button>
          )}
        </header>

        {/* Mobile "More" sheet — extra destinations not on the bottom tab bar */}
        {more ? (
          <div className="fixed inset-0 z-50 md:hidden" data-testid="mobile-more-sheet">
            <div className="absolute inset-0 bg-ink-900/40" onClick={() => setMore(false)} />
            <div
              className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white shadow-2xl"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1rem)" }}
            >
              <div className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
                <p className="font-heading text-base font-bold text-ink-900">More</p>
                <button
                  type="button"
                  onClick={() => setMore(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-500 hover:bg-ink-100"
                  aria-label="Close"
                  data-testid="mobile-more-close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-1 p-3">
                {nav.filter((i) => !i.mobile).map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    onClick={() => setMore(false)}
                    className={({ isActive }) =>
                      `flex h-12 items-center gap-3 rounded-lg px-3 text-base font-semibold ${
                        isActive ? "bg-brand-50 text-brand" : "text-ink-700 hover:bg-ink-100"
                      }`
                    }
                    data-testid={`more-${label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Icon className="h-5 w-5" /> {label}
                  </NavLink>
                ))}
                <div className="my-2 h-px bg-ink-100" />
                <button
                  type="button"
                  onClick={doLogout}
                  className="flex h-12 w-full items-center gap-3 rounded-lg px-3 text-base font-semibold text-rose-700 hover:bg-rose-50"
                  data-testid="more-logout"
                >
                  <LogOut className="h-5 w-5" /> Log out
                </button>
                <p className="px-3 pt-2 text-xs text-ink-500">
                  Signed in as <span className="font-semibold text-ink-700">{user?.email}</span>
                </p>
              </div>
            </div>
          </div>
        ) : null}

        <main
          className="flex-1 px-4 py-5 sm:px-6 lg:px-8"
          style={{ paddingBottom: "calc(5rem + max(env(safe-area-inset-bottom), 0px))" }}
        >
          <div className="mx-auto max-w-7xl">
            <VerifyEmailBanner />
            {children}
          </div>
        </main>

        <BottomTabBar items={nav} />
      </div>
      {/* Add-to-Home-Screen prompt. Only signed-in mobile users on their
          dashboards see it — public / marketing pages never do. */}
      <InstallPrompt />
    </div>
  );
}
